import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Key, Lock, Shield, Map, Zap, DoorOpen, Crosshair, Heart, Cpu, KeyRound, Radio, Unlock, AlertCircle, AlertTriangle, Check, Fingerprint, Music, Terminal } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { PUZZLE_6_FINAL_LOCK } from '@/config/missionContent';

const SLOTS = [
  { id: 'lab_keycard', label: 'ALPHA', icon: Key, expectedItem: 'lab_keycard', color: '#22c55e', angle: 0 },
  { id: 'tactical_scope', label: 'TARGET', icon: Crosshair, expectedItem: 'tactical_scope', color: '#ef4444', angle: 33 },
  { id: 'vault_key', label: 'VAULT', icon: Lock, expectedItem: 'vault_key', color: '#eab308', angle: 65 },
  { id: 'first_aid_kit', label: 'MED', icon: Heart, expectedItem: 'first_aid_kit', color: '#ec4899', angle: 98 },
  { id: 'security_badge', label: 'CLEAR', icon: Shield, expectedItem: 'security_badge', color: '#a855f7', angle: 131 },
  { id: 'access_chip', label: 'DATA', icon: Cpu, expectedItem: 'access_chip', color: '#3b82f6', angle: 164 },
  { id: 'encrypted_map', label: 'NAV', icon: Map, expectedItem: 'encrypted_map', color: '#14b8a6', angle: 196 },
  { id: 'master_key', label: 'MASTER', icon: KeyRound, expectedItem: 'master_key', color: '#f97316', angle: 229 },
  { id: 'power_cell', label: 'POWER', icon: Zap, expectedItem: 'power_cell', color: '#84cc16', angle: 262 },
  { id: 'signal_beacon', label: 'COMMS', icon: Radio, expectedItem: 'signal_beacon', color: '#06b6d4', angle: 295 },
  { id: 'music_box_key', label: 'MELODY', icon: Music, expectedItem: 'music_box_key', color: '#fb7185', angle: 327 },
];

const INVENTORY_ITEMS = [
  { ...ITEMS.LAB_KEYCARD, Icon: Key },
  { ...ITEMS.TACTICAL_SCOPE, Icon: Crosshair },
  { ...ITEMS.VAULT_KEY, Icon: Lock },
  { ...ITEMS.FIRST_AID_KIT, Icon: Heart },
  { ...ITEMS.SECURITY_BADGE, Icon: Shield },
  { ...ITEMS.ACCESS_CHIP, Icon: Cpu },
  { ...ITEMS.ENCRYPTED_MAP, Icon: Map },
  { ...ITEMS.MASTER_KEY, Icon: KeyRound },
  { ...ITEMS.POWER_CELL, Icon: Zap },
  { ...ITEMS.SIGNAL_BEACON, Icon: Radio },
  { ...ITEMS.MUSIC_BOX_KEY, Icon: Music },
];

/* ── VaultBackground ─────────────────────────────────── */
function VaultBackground({ progress, solved }) {
  const floatingParticles = useMemo(() =>
    Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: `${10 + (i * 3.2) % 80}%`,
      top: `${10 + (i * 3.7 + 5) % 80}%`,
      dur: 3 + (i * 0.6) % 2,
      delay: (i * 0.4) % 2,
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Dark green base */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#010a05] to-black" />

      {/* Vault door circles */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {/* Outer decorative ring */}
        <motion.div
          animate={solved ? { rotate: 90, scale: 1.1 } : { rotate: 0 }}
          transition={{ duration: 2, ease: 'easeInOut' }}
          className="absolute -inset-40 rounded-full border-2 border-green-900/20"
          style={{ borderStyle: 'dashed' }}
        />

        {/* Main vault ring */}
        <svg className="absolute -inset-32 w-[calc(100%+256px)] h-[calc(100%+256px)]" viewBox="0 0 400 400">
          {/* Background circle */}
          <circle cx="200" cy="200" r="180" fill="none" stroke="rgba(0,255,65,0.1)" strokeWidth="4" />

          {/* Progress arc */}
          <motion.circle
            cx="200" cy="200" r="180"
            fill="none"
            stroke="rgba(0,255,65,0.5)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${progress * (2 * Math.PI * 180 / SLOTS.length)} ${2 * Math.PI * 180}`}
            transform="rotate(-90 200 200)"
            style={{ filter: 'drop-shadow(0 0 10px rgba(0,255,65,0.5))' }}
          />

          {/* Lock indicators around the ring */}
          {SLOTS.map((slot, i) => {
            const angle = (i / SLOTS.length) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const x = 200 + Math.cos(rad) * 180;
            const y = 200 + Math.sin(rad) * 180;
            const isActive = i < progress;

            return (
              <g key={i}>
                <circle
                  cx={x} cy={y} r="8"
                  fill={isActive ? slot.color : 'rgba(100,100,100,0.3)'}
                  style={{ filter: isActive ? `drop-shadow(0 0 8px ${slot.color})` : 'none' }}
                />
                {isActive && (
                  <motion.circle
                    cx={x} cy={y} r="12"
                    fill="none"
                    stroke={slot.color}
                    strokeWidth="2"
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 2, opacity: 0 }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* Inner ring */}
        <motion.div
          animate={solved ? { rotate: -45 } : { rotate: 0 }}
          transition={{ duration: 2, ease: 'easeInOut' }}
          className="absolute -inset-16 rounded-full border border-green-800/30"
        />

        {/* Center glow */}
        <div
          className="absolute -inset-8 rounded-full"
          style={{
            background: `radial-gradient(circle, rgba(0,255,65,${0.05 + progress * 0.01}) 0%, transparent 70%)`
          }}
        />
      </div>

      {/* Floating particles (memoized) */}
      {floatingParticles.map(p => (
        <motion.div
          key={p.id}
          className="absolute w-1 h-1 rounded-full bg-green-500/40"
          style={{ left: p.left, top: p.top }}
          animate={{ y: [0, -30, 0], opacity: [0, 0.6, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity }}
        />
      ))}

      {/* Green pulsing vignette */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,20,0,0.6) 100%)' }}
      />

      {/* CRT scanlines */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.06) 2px, rgba(0,255,65,0.06) 4px)',
          backgroundSize: '100% 4px',
        }}
      />
    </div>
  );
}

/* ── VaultTutorial ───────────────────────────────────── */
function VaultTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-md mx-auto"
    >
      <div className="bg-black/70 border border-green-800/40 rounded-lg p-6 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-green-900/40 border border-green-600/30 flex items-center justify-center">
            <Lock size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-green-400 text-sm tracking-wider">VAULT AUTHORIZATION</h3>
            <p className="text-xs font-terminal text-green-700 tracking-widest mt-0.5">FINAL SECURITY CLEARANCE</p>
          </div>
        </div>

        {/* Procedure */}
        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">PROCEDURE</p>
          <div className="space-y-2.5">
            {[
              { n: '01', text: 'Select an item from your INVENTORY below' },
              { n: '02', text: 'Place it in the matching VAULT SLOT on the ring' },
              { n: '03', text: 'Fill all 11 slots to unseal the vault' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <span className="text-xs font-orbitron text-green-500 mt-0.5">{step.n}</span>
                <p className="text-xs text-gray-300 font-rajdhani">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Slot identification */}
        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">SLOT IDENTIFICATION</p>
          <div className="flex flex-wrap gap-3">
            {SLOTS.map(slot => (
              <div key={slot.id} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: slot.color, boxShadow: `0 0 6px ${slot.color}66` }} />
                <span className="text-xs font-terminal tracking-wider" style={{ color: slot.color }}>{slot.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-center gap-2 bg-yellow-950/20 border border-yellow-900/30 rounded px-3 py-2 mb-5">
          <AlertTriangle size={12} className="text-yellow-400 shrink-0" />
          <span className="text-xs font-terminal text-yellow-400 tracking-wider">WRONG PLACEMENTS ARE TRACKED — PRECISION MATTERS</span>
        </div>

        {/* Start button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStart}
          className="w-full py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #22c55e, #4ade80)' }}
        >
          <span className="relative z-10">INITIATE SEQUENCE</span>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)', width: '50%' }}
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── VaultHUD ────────────────────────────────────────── */
function VaultHUD({ placedCount, attempts }) {
  return (
    <div className="max-w-2xl mx-auto mb-4 px-4 relative z-10">
      <div className="flex items-center justify-between bg-black/40 border border-green-900/30 rounded px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-terminal text-green-700 tracking-wider">FINAL CHECKPOINT // MAIN VAULT</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Lock size={10} className="text-green-500" />
            <span className="text-xs font-orbitron text-green-400">{placedCount}<span className="text-gray-600">/{SLOTS.length}</span></span>
          </div>
          {attempts > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle size={10} className="text-red-400" />
              <span className="text-xs font-orbitron text-red-400">{attempts}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── VaultSlots ──────────────────────────────────────── */
function VaultSlots({ slots, placedItems, selectedItem, wrongSlot, onSlotClick, solved }) {
  const SLOT_SIZE = 64;
  const HALF = SLOT_SIZE / 2;

  return (
    <div className="relative mx-auto" style={{ width: 420, height: 420 }}>
      {/* Center vault icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          {/* Expanding glow ring on solve */}
          {solved && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0.8 }}
              animate={{ scale: 2.5, opacity: 0 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full border-2 border-green-400"
            />
          )}

          <motion.div
            animate={solved
              ? { boxShadow: ['0 0 40px rgba(34,197,94,0.3)', '0 0 60px rgba(34,197,94,0.5)', '0 0 40px rgba(34,197,94,0.3)'] }
              : {}
            }
            transition={solved ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : {}}
            className={`w-24 h-24 rounded-full flex items-center justify-center border-4 ${
              solved ? 'border-green-500 bg-green-500/20' : 'border-green-500/30 bg-green-950/20'
            }`}
            style={{
              boxShadow: solved
                ? '0 0 40px rgba(34,197,94,0.4), inset 0 0 20px rgba(34,197,94,0.2)'
                : '0 0 30px rgba(0,255,65,0.2), inset 0 0 20px rgba(0,255,65,0.1)'
            }}
          >
            <AnimatePresence mode="wait">
              {solved ? (
                <motion.div
                  key="unlocked"
                  initial={{ scale: 0, rotate: -90 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                >
                  <Unlock size={40} className="text-green-400" />
                </motion.div>
              ) : (
                <motion.div
                  key="locked"
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Fingerprint size={40} className="text-green-400" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Slots arranged in a circle */}
      {slots.map((slot, i) => {
        const placed = placedItems[slot.id];
        const isWrong = wrongSlot === slot.id;
        const SlotIcon = slot.icon;
        const angle = (i / SLOTS.length) * 360 - 90;
        const rad = (angle * Math.PI) / 180;
        const radius = 175;
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;
        const canClick = selectedItem && !placed && !solved;

        return (
          <motion.button
            key={slot.id}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              x: isWrong ? [0, -5, 5, -5, 0] : 0
            }}
            transition={{
              delay: i * 0.05,
              x: { duration: 0.3 }
            }}
            whileHover={canClick ? { scale: 1.12 } : {}}
            whileTap={canClick ? { scale: 0.92 } : {}}
            onClick={() => onSlotClick(slot)}
            className="absolute rounded-2xl flex flex-col items-center justify-center gap-1 transition-all"
            style={{
              width: SLOT_SIZE,
              height: SLOT_SIZE,
              left: `calc(50% + ${x}px - ${HALF}px)`,
              top: `calc(50% + ${y}px - ${HALF}px)`,
              background: placed
                ? `linear-gradient(135deg, ${slot.color}20, ${slot.color}08)`
                : canClick
                  ? 'linear-gradient(135deg, rgba(0,255,65,0.08), rgba(0,0,0,0.4))'
                  : 'linear-gradient(135deg, rgba(30,30,30,0.6), rgba(10,10,10,0.8))',
              border: placed
                ? `2px solid ${slot.color}88`
                : canClick
                  ? '2px solid rgba(0,255,65,0.35)'
                  : '2px solid rgba(60,60,60,0.5)',
              boxShadow: placed
                ? `0 0 20px ${slot.color}30, inset 0 0 12px ${slot.color}10`
                : canClick
                  ? '0 0 15px rgba(0,255,65,0.12), inset 0 1px 0 rgba(255,255,255,0.03)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.02)',
            }}
            disabled={!!placed || solved || !selectedItem}
            data-testid={`lock-slot-${slot.id}`}
          >
            <SlotIcon
              size={24}
              style={{
                color: placed ? slot.color : canClick ? slot.color : '#4b5563',
                filter: placed ? `drop-shadow(0 0 4px ${slot.color}60)` : 'none',
              }}
            />
            <span className="text-xs font-terminal tracking-widest"
              style={{ color: placed ? slot.color : canClick ? 'rgba(0,255,65,0.6)' : '#4b5563' }}>
              {slot.label}
            </span>

            {/* Check mark overlay */}
            {placed && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: slot.color, boxShadow: `0 0 10px ${slot.color}80` }}
              >
                <Check size={12} className="text-black" />
              </motion.div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

/* ── ItemInventory ───────────────────────────────────── */
function ItemInventory({ items, selectedItem, isItemPlaced, onItemClick }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-terminal text-green-600 tracking-widest">COLLECTED ITEMS</span>
        {selectedItem && (
          <motion.span
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-xs font-terminal text-green-400"
          >
            Selected: {selectedItem.name}
          </motion.span>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        {items.map((item) => {
          const placed = isItemPlaced(item.id);
          const isSelected = selectedItem?.id === item.id;
          const slot = SLOTS.find(s => s.expectedItem === item.id);
          const color = slot?.color || '#888';

          return (
            <motion.button
              key={item.id}
              whileHover={!placed ? { scale: 1.05, y: -2 } : {}}
              whileTap={!placed ? { scale: 0.95 } : {}}
              onClick={() => onItemClick(item)}
              disabled={placed}
              className={`flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-lg border-2 transition-all relative ${
                placed
                  ? 'opacity-30 border-gray-700 cursor-default'
                  : isSelected
                    ? 'border-green-400 bg-green-500/20'
                    : 'border-gray-700 hover:border-green-500/50 bg-black/30'
              }`}
              style={{
                boxShadow: isSelected ? `0 0 20px ${color}50` : 'none'
              }}
              data-testid={`lock-item-${item.id}`}
            >
              <item.Icon
                size={26}
                style={{ color: placed ? '#4b5563' : isSelected ? color : '#9ca3af' }}
              />
              <span className="text-xs font-terminal tracking-wider truncate w-full text-center"
                style={{ color: placed ? '#4b5563' : isSelected ? color : '#6b7280' }}>
                {item.name.split(' ').slice(-1)[0].toUpperCase()}
              </span>

              {/* Pulse effect when selected */}
              {isSelected && (
                <motion.div
                  className="absolute inset-0 rounded-lg border-2"
                  style={{ borderColor: color }}
                  animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

/* ── ProgressDisplay ─────────────────────────────────── */
function ProgressDisplay({ count, total }) {
  const percentage = (count / total) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-terminal text-green-600 tracking-widest">VAULT AUTHORIZATION</span>
        <span className={`font-orbitron ${count === total ? 'text-green-400' : 'text-green-400'}`}>
          {count}/{total}
        </span>
      </div>

      <div className="h-3 bg-black/60 rounded-full overflow-hidden border border-green-900/30">
        <motion.div
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{
            background: count === total
              ? 'linear-gradient(90deg, #22c55e, #4ade80)'
              : 'linear-gradient(90deg, #22c55e, #4ade80)',
            boxShadow: count === total
              ? '0 0 15px rgba(34,197,94,0.5)'
              : '0 0 10px rgba(0,255,65,0.4)'
          }}
        />
      </div>
    </div>
  );
}

/* ── VaultTerminal ───────────────────────────────────── */
function VaultTerminal({ log }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [log.length]);

  return (
    <div className="bg-black/80 border border-green-900/30 rounded-lg overflow-hidden">
      {/* Terminal header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-green-900/20 bg-green-950/20">
        <Terminal size={10} className="text-green-600" />
        <span className="text-xs font-terminal text-green-600 tracking-wider">VAULT_SYSTEM v2.1 — AUTHORIZATION LOG</span>
      </div>

      {/* Log entries */}
      <div ref={scrollRef} className="px-3 py-2 max-h-[120px] overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
        {log.length === 0 ? (
          <p className="text-xs font-terminal text-green-800 animate-pulse">&gt; Awaiting item placement...</p>
        ) : (
          log.map((entry, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="text-xs font-terminal leading-relaxed"
            >
              <span className="text-green-800">[{entry.time}]</span>{' '}
              <span className="text-green-500">SLOT {entry.slotLabel}</span>{' '}
              <span className="text-green-700">←</span>{' '}
              <span className="text-gray-400">{entry.itemName}</span>{' '}
              <span className="text-green-700">...</span>{' '}
              {entry.type === 'success' ? (
                <span className="text-green-400">VERIFIED ✓</span>
              ) : (
                <span className="text-red-400">REJECTED ✗</span>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────── */
export default function Puzzle6FinalLock({ onComplete }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [placedItems, setPlacedItems] = useState({});
  const [wrongSlot, setWrongSlot] = useState(null);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [terminalLog, setTerminalLog] = useState([]);

  /* Mount-reset */
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setActive(false);
    setSelectedItem(null);
    setPlacedItems({});
    setWrongSlot(null);
    setSolved(false);
    setShowFinalLeon(false);
    setSuccessPhase(0);
    setShowProceed(false);
    setAttempts(0);
    setTerminalLog([]);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setActive(true);
  }, []);

  const isItemPlaced = useCallback((itemId) =>
    Object.values(placedItems).includes(itemId)
  , [placedItems]);

  const handleItemClick = useCallback((item) => {
    if (!active || solved || isItemPlaced(item.id)) return;
    setSelectedItem(item);
    setWrongSlot(null);
  }, [active, solved, isItemPlaced]);

  const handleSlotClick = useCallback((slot) => {
    if (!active || solved || !selectedItem || placedItems[slot.id]) return;

    const logTime = new Date().toLocaleTimeString('en-GB');

    if (selectedItem.id === slot.expectedItem) {
      setTerminalLog(prev => [...prev, { type: 'success', slotLabel: slot.label, itemName: selectedItem.name, time: logTime }]);
      const newPlaced = { ...placedItems, [slot.id]: selectedItem.id };
      setPlacedItems(newPlaced);
      setSelectedItem(null);

      if (Object.keys(newPlaced).length === SLOTS.length) {
        setSolved(true);
        setActive(false);
        /* Multi-phase success */
        setSuccessPhase(1);
        setTimeout(() => setSuccessPhase(2), 500);
        setTimeout(() => setSuccessPhase(3), 1200);
        setTimeout(() => setSuccessPhase(4), 1800);
        setTimeout(() => setSuccessPhase(5), 2500);
        setTimeout(() => setShowFinalLeon(true), 3200);
      }
    } else {
      setTerminalLog(prev => [...prev, { type: 'error', slotLabel: slot.label, itemName: selectedItem.name, time: logTime }]);
      setWrongSlot(slot.id);
      setAttempts(a => a + 1);
      setTimeout(() => setWrongSlot(null), 600);
    }
  }, [active, solved, selectedItem, placedItems]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    setShowProceed(true);
  }, []);

  const placedCount = Object.keys(placedItems).length;

  return (
    <div className="puzzle-container relative min-h-screen" data-testid="puzzle-final-lock">
      <VaultBackground progress={placedCount} solved={solved} />

      {/* Header */}
      <div className="relative z-10 text-center pt-8 pb-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-3 mb-3"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1, opacity: [0.6, 1, 0.6] }}
            transition={{ scale: { type: 'spring', stiffness: 300, damping: 15 }, opacity: { duration: 3, repeat: Infinity, ease: 'easeInOut' } }}
          >
            <Lock size={16} className="text-green-500" />
          </motion.div>
          <span className="text-xs font-terminal tracking-[0.4em] text-green-600">FINAL CHECKPOINT</span>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1, opacity: [0.6, 1, 0.6] }}
            transition={{ scale: { type: 'spring', stiffness: 300, damping: 15, delay: 0.1 }, opacity: { duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.5 } }}
          >
            <Shield size={16} className="text-green-500" />
          </motion.div>
        </motion.div>

        <h1 className="text-4xl md:text-5xl font-orbitron font-bold tracking-[0.15em] text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-green-300 to-green-400 mb-2">
          MAIN VAULT
        </h1>
        <p className="text-green-700 font-terminal text-sm tracking-wider">
          Place all 11 items to unlock
        </p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <VaultTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(active || solved) && (
        <VaultHUD placedCount={placedCount} attempts={attempts} />
      )}

      {/* Main content */}
      {(active || solved) && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-2xl mx-auto px-4 space-y-6"
        >
          {/* Progress bar */}
          <ProgressDisplay count={placedCount} total={SLOTS.length} />

          {/* Vault panel */}
          <div className="bg-gradient-to-b from-gray-900/90 to-black/90 rounded-3xl p-6 border border-green-900/30"
            style={{ boxShadow: '0 0 60px rgba(0,0,0,0.5)' }}>

            {/* Vault slots */}
            <VaultSlots
              slots={SLOTS}
              placedItems={placedItems}
              selectedItem={selectedItem}
              wrongSlot={wrongSlot}
              onSlotClick={handleSlotClick}
              solved={solved}
            />

            {/* Wrong slot warning */}
            <AnimatePresence>
              {wrongSlot && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-2 mt-4 bg-red-950/30 border border-red-500/30 rounded-lg px-4 py-2"
                >
                  <AlertCircle size={16} className="text-red-400" />
                  <span className="text-sm font-terminal text-red-400">INCORRECT SLOT</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Terminal log */}
          {!solved && <VaultTerminal log={terminalLog} />}

          {/* Item inventory */}
          {!solved && (
            <div className="bg-gradient-to-b from-gray-900/90 to-black/90 rounded-2xl p-5 border border-green-900/30">
              <ItemInventory
                items={INVENTORY_ITEMS}
                selectedItem={selectedItem}
                isItemPlaced={isItemPlaced}
                onItemClick={handleItemClick}
              />

              {!selectedItem && (
                <p className="text-center text-xs text-gray-500 font-terminal mt-3">
                  Select an item above, then place it in the matching slot
                </p>
              )}
            </div>
          )}

          {/* Multi-phase success */}
          {successPhase >= 1 && (
            <div className="text-center mt-4 relative">
              {/* Phase 1: Green flash + shockwave */}
              <AnimatePresence>
                {successPhase >= 1 && successPhase < 2 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative">
                    <motion.div
                      initial={{ scale: 0.3, opacity: 0.8 }}
                      animate={{ scale: 3, opacity: 0 }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="absolute left-1/2 top-10 -translate-x-1/2 w-20 h-20 rounded-full border-2 border-green-400"
                    />
                    <motion.div
                      initial={{ opacity: 0.6 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 bg-green-400/10 rounded-lg"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Phase 2: DoorOpen icon */}
              {successPhase >= 2 && (
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
                  />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                      style={{ boxShadow: '0 0 25px rgba(0,255,65,0.2)' }}>
                      <DoorOpen size={28} className="text-green-400" />
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Phase 3: VAULT UNSEALED */}
              {successPhase >= 3 && (
                <motion.p
                  initial={{ letterSpacing: '2px', opacity: 0 }}
                  animate={{ letterSpacing: '5px', opacity: 1 }}
                  className="font-orbitron text-green-400 text-sm mb-3"
                >
                  VAULT UNSEALED
                </motion.p>
              )}

              {/* Phase 4: Stats + personal message */}
              {successPhase >= 4 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-center gap-6 text-xs font-terminal">
                    <span className="text-green-500">ITEMS <span className="text-green-300 font-orbitron">{SLOTS.length}/{SLOTS.length}</span></span>
                    <span className="text-gray-500">WRONG ATTEMPTS <span className={`font-orbitron ${attempts === 0 ? 'text-green-300' : 'text-red-400'}`}>{attempts}</span></span>
                  </div>
                  <p className="text-sm font-terminal" style={{ color: 'rgba(0,255,65,0.4)' }}>
                    The vault door swings open, revealing:
                  </p>
                  <p className="text-base font-rajdhani italic" style={{ color: '#4ade80' }}>
                    "Everything behind this door was always meant for you, {CONFIG.recipientName}."
                  </p>
                </motion.div>
              )}

              {/* Leon dialogue auto-triggers at successPhase 5 */}
            </div>
          )}

          {showProceed && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              className="text-center mt-8 relative z-20"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onComplete?.()}
                className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
              >
                <span className="relative z-10">PROCEED</span>
                <motion.div
                  className="absolute inset-0"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
                />
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={PUZZLE_6_FINAL_LOCK.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={PUZZLE_6_FINAL_LOCK.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
